import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const StatsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState('');
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLists();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (selectedList) {
      fetchListStats(selectedList);
    }
  }, [selectedList]);

  const fetchLists = async () => {
    try {
      const response = await api.get('/lists');
      setLists(response.data);
      if (response.data.length > 0) {
        setSelectedList(response.data[0].id.toString());
      }
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchListStats = async (listId) => {
    try {
      const response = await api.get(`/stats/list/${listId}`);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch list stats:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/stats/history');
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const boxColors = ['#EF4444', '#F59E0B', '#FBBF24', '#84CC16', '#22C55E'];

  return (
    <div>
      <h1 className="page-title">{t('stats.title')}</h1>

      {error && (
        <div className="form-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Overview cards */}
      {history && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>
              {history.today?.totalReviewed || 0}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              {t('stats.today')}
            </div>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>
              {history.week?.totalReviewed || 0}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              {t('stats.thisWeek')}
            </div>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>
              {history.overall?.totalReviewed || 0}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              {t('stats.total')}
            </div>
          </div>
        </div>
      )}

      {/* List selector with details button - Feature #139 */}
      {lists.length > 0 && (
        <div className="form-group">
          <label htmlFor="list" className="form-label">
            {t('training.selectList')}
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select
              id="list"
              className="form-input"
              value={selectedList}
              onChange={(e) => setSelectedList(e.target.value)}
              style={{ flex: 1 }}
            >
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn-outline"
              onClick={() => navigate(`/lists/${selectedList}`)}
              disabled={!selectedList}
              title={t('stats.details') || 'Details'}
            >
              {t('stats.details') || 'Details'}
            </button>
          </div>
        </div>
      )}

      {/* List stats */}
      {stats && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>{t('stats.boxDistribution')}</h2>

          {/* Progress percentage */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>{t('stats.learned')}</span>
              <span>{stats.progressPercent || 0}%</span>
            </div>
            <div className="progress-bar" style={{ height: '12px' }}>
              <div
                className="progress-bar-fill"
                style={{
                  width: `${stats.progressPercent || 0}%`,
                  backgroundColor: 'var(--color-success)',
                }}
              ></div>
            </div>
          </div>

          {/* Box distribution bars */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[0, 1, 2, 3, 4].map((index) => {
              const count = stats.boxDistribution?.[index] || 0;
              const total = stats.totalCards || 1;
              const percentage = (count / total) * 100;

              return (
                <div key={index}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.875rem' }}>
                      {t(`stats.box${index + 1}`)}
                    </span>
                    <span style={{ fontSize: '0.875rem' }}>{count}</span>
                  </div>
                  <div
                    style={{
                      height: '8px',
                      backgroundColor: 'var(--color-surface)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${percentage}%`,
                        backgroundColor: boxColors[index],
                        transition: 'width 0.3s ease',
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {lists.length === 0 && (
        <div className="empty-state">
          <p>{t('lists.empty')}</p>
        </div>
      )}
    </div>
  );
};

export default StatsPage;
